/**
 * Activity Tracking Hook
 * Tracks user activity for idle timeout management
 */

import { useEffect, useRef, useCallback } from 'react';
import { useSession, useAuthStore } from '@/store/authStore';

// Events that indicate user activity
const ACTIVITY_EVENTS = [
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'mousemove',
  'click',
] as const;

// Debounce delay for activity updates (ms)
const ACTIVITY_DEBOUNCE_MS = 1000;

// Idle timeout duration (30 minutes)
const IDLE_TIMEOUT_MS = 30 * 60 * 1000;

// Warning before timeout (5 minutes)
const WARNING_BEFORE_MS = 5 * 60 * 1000;

interface UseActivityTrackingOptions {
  enabled?: boolean;
  onIdle?: () => void;
  onWarning?: () => void;
  idleTimeoutMs?: number;
  warningBeforeMs?: number;
}

/**
 * Hook to track user activity and handle idle timeout
 */
export function useActivityTracking(options: UseActivityTrackingOptions = {}) {
  const {
    enabled = true,
    onIdle,
    onWarning,
    idleTimeoutMs = IDLE_TIMEOUT_MS,
    warningBeforeMs = WARNING_BEFORE_MS,
  } = options;

  const { updateLastActivity, lastActivity } = useSession();
  const logout = useAuthStore((state) => state.logout);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const idleTimerRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityUpdateRef = useRef<number>(0);

  // Debounced activity update
  const handleActivity = useCallback(() => {
    const now = Date.now();
    
    // Debounce activity updates
    if (now - lastActivityUpdateRef.current < ACTIVITY_DEBOUNCE_MS) {
      return;
    }
    
    lastActivityUpdateRef.current = now;
    updateLastActivity();

    // Reset timers
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
    }

    // Set warning timer
    warningTimerRef.current = setTimeout(() => {
      onWarning?.();
    }, idleTimeoutMs - warningBeforeMs);

    // Set idle timer
    idleTimerRef.current = setTimeout(() => {
      onIdle?.();
      // Auto logout on idle
      logout();
    }, idleTimeoutMs);
  }, [updateLastActivity, idleTimeoutMs, warningBeforeMs, onIdle, onWarning, logout]);

  // Setup activity listeners
  useEffect(() => {
    if (!enabled || !isAuthenticated) {
      return;
    }

    // Initial activity
    handleActivity();

    // Add event listeners
    ACTIVITY_EVENTS.forEach((event) => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      // Cleanup event listeners
      ACTIVITY_EVENTS.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });

      // Clear timers
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
      }
    };
  }, [enabled, isAuthenticated, handleActivity]);

  // Handle visibility change (tab focus)
  useEffect(() => {
    if (!enabled || !isAuthenticated) {
      return;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Tab became visible - check if we've been idle too long
        const now = Date.now();
        const idleDuration = now - lastActivity;

        if (idleDuration >= idleTimeoutMs) {
          // Idle timeout exceeded while tab was hidden
          onIdle?.();
          logout();
        } else {
          // Resume activity tracking
          handleActivity();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, isAuthenticated, lastActivity, idleTimeoutMs, onIdle, logout, handleActivity]);

  return {
    lastActivity,
    resetActivity: handleActivity,
  };
}

export default useActivityTracking;

